import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, type Me } from './api';

interface AuthCtx {
  me: Me['user'];
  mfaVerified: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ me: Me['user']; mfaVerified: boolean; loading: boolean }>({
    me: null,
    mfaVerified: false,
    loading: true,
  });

  const refresh = async () => {
    try {
      const data = await api.get<Me>('/auth/me');
      setState({ me: data.user, mfaVerified: data.mfaVerified, loading: false });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setState({ me: null, mfaVerified: false, loading: false });
      } else {
        setState((s) => ({ ...s, loading: false }));
      }
    }
  };

  useEffect(() => { void refresh(); }, []);

  const logout = async () => {
    try { await api.post('/auth/logout'); } finally {
      setState({ me: null, mfaVerified: false, loading: false });
    }
  };

  return <Ctx.Provider value={{ ...state, refresh, logout }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth außerhalb AuthProvider');
  return v;
}
